import { Product, ProductAvailability } from "../../../graphql/operations";
import { FullName } from "./Parts/ContactPicker/NameInput";

interface ProductInitialState {
  details: Array<{
    typeId: string;
    quantity: number;
    customerType: string;
    price: number;
    currency: string;
    excludeFromPrivateGroupCount: boolean;
  }>;
  reservationDate: Date | null;
  reservationTime: string | null;
  specialRequests: string | null;
}

export function mapProductToFormInitialState(
  product: Product
): ProductInitialState {
  const { prices } = product;

  const details = prices.map((price) => ({
    typeId: price.id,
    quantity: price.minimumPeople,
    customerType: price.translation.name,
    price: price.amount,
    currency: price.currency,
    excludeFromPrivateGroupCount: price.excludeFromPrivateGroupCount,
  }));

  return {
    details,
    reservationDate: null,
    reservationTime: null,
    specialRequests: "",
  };
}

interface ContactInformation {
  names: FullName[];
  email: string | null;
  confirmEmail: string | null;
  contactNumber: string | null;
  acceptsMarketing: boolean;
}

export function mapUserProfileToFormInitialState(
  contactInformation: ContactInformation,
  couponCode: string | null
) {
  return {
    ...contactInformation,
    ...(couponCode ? { couponCode } : null),
    acceptsMarketing: false,
  };
}

interface LocationQuery {
  date?: string;
  time?: string;
  groupId?: string;
  variant?: string;
  coupon?: string;
  guideId?: string;
}

function getFirstAvailableDate(date, availableDates) {
  if (date && availableDates.filter((d) => d.date === date).length > 0) {
    return availableDates.filter((d) => d.date === date)[0].date;
  } else {
    return availableDates[0]?.date;
  }
}
function getFirstAvailableTime(time, availableDates, date) {
  if (
    time &&
    date &&
    availableDates.filter((d) => d.date === date).length > 0
  ) {
    const timeSlotValid = availableDates
      .filter((d) => d.date === date)[0]
      .timeSlots.filter((t) => t.time === time)[0]?.time;
    if (timeSlotValid) {
      return timeSlotValid;
    } else {
      return availableDates[0].timeSlots.filter(
        (t) => t.status === "AVAILABLE"
      )[0].time;
    }
  } else {
    return availableDates[0]?.timeSlots.filter(
      (t) => t.status === "AVAILABLE"
    )[0].time;
  }
}
export function mapQueryToFormInitialState(
  query: LocationQuery,
  productInitialState: ProductInitialState,
  availability: any,
  couponCode: string | null,
  guideId: string | null
) {
  const availableDates = availability.filter((s) => s.status === "AVAILABLE");

  return {
    reservationDate: getFirstAvailableDate(query.date, availableDates),
    reservationTime: getFirstAvailableTime(
      query.time,
      availableDates,
      query.date
    ),

    presetVariant: query.variant,
    couponCode: query.coupon ? query.coupon : couponCode,
    guideId: query.guideId ? query.guideId : guideId,
  };
}

type ApolloQueryData = {
  product: Product;
  productAvailability: ProductAvailability;
};

type Props = {
  apolloData: ApolloQueryData;
  query: LocationQuery;
  contactInformation: ContactInformation;
  couponCode: string | null;
  guideId: string | null;
  campaignSource?: string;
};

export function getInitialState({
  apolloData,
  query,
  contactInformation,
  couponCode,
  campaignSource,
  guideId,
}: Props) {
  const { product } = apolloData;

  const productInitialState = mapProductToFormInitialState(product);

  return {
    ...mapUserProfileToFormInitialState(contactInformation, couponCode),
    ...productInitialState,
    ...mapQueryToFormInitialState(
      query,
      productInitialState,
      product.availability,
      couponCode,
      guideId
    ),
    campaignSource,
  };
}
